import { ChangeEvent, useRef } from "react";
import { toast } from "sonner";
import { GripVertical, Star, Trash2, Upload } from "lucide-react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  rectSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Button } from "@/components/ui/button";
import {
  BusinessEntity,
  useDeleteBusinessPhoto,
  useReorderBusinessPhotos,
  useSetCoverPhoto,
  useUploadBusinessPhotos,
} from "@/hooks/useBusinesses";

const ROLE_LABELS = ["Thumbnail", "Detail", "Detail"] as const;
const getRole = (idx: number) => ROLE_LABELS[idx] ?? "Gallery";

interface PhotoTileProps {
  photo: { id: string; url: string; isCover: boolean };
  index: number;
  onDelete: () => void;
  onSetCover: () => void;
  delPending: boolean;
  coverPending: boolean;
}

const PhotoTile = ({
  photo,
  index,
  onDelete,
  onSetCover,
  delPending,
  coverPending,
}: PhotoTileProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: photo.id });
  const role = getRole(index);
  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
      }}
      className={`relative rounded-card overflow-hidden border ${photo.isCover ? "border-primary ring-2 ring-primary/30" : "border-border"}`}
    >
      <img src={photo.url} alt="" className="w-full h-28 object-cover" />
      <button
        type="button"
        className="absolute top-1 left-1 p-0.5 bg-black/50 rounded text-white/70 hover:text-white cursor-grab active:cursor-grabbing"
        {...attributes}
        {...listeners}
        aria-label="Drag to reorder"
      >
        <GripVertical className="w-3.5 h-3.5" />
      </button>
      <span className="absolute top-1 right-1 text-[9px] font-semibold bg-black/60 text-white px-1.5 py-0.5 rounded">
        {role}
      </span>
      <div className="absolute inset-x-0 bottom-0 flex justify-between p-1.5 bg-gradient-to-t from-black/70 to-transparent">
        <button
          type="button"
          onClick={onSetCover}
          disabled={photo.isCover || coverPending}
          className="flex items-center gap-1 text-[11px] text-white"
          title="Set as thumbnail"
        >
          <Star
            className={`w-3.5 h-3.5 ${photo.isCover ? "fill-amber-400 text-amber-400" : ""}`}
          />
          {photo.isCover ? "Thumbnail" : "Set thumbnail"}
        </button>
        <button
          type="button"
          onClick={onDelete}
          disabled={delPending}
          className="text-white/80 hover:text-white"
          title="Remove"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};

interface Props {
  b: BusinessEntity;
}

export const PhotosPanel = ({ b }: Props) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const upload = useUploadBusinessPhotos();
  const del = useDeleteBusinessPhoto();
  const cover = useSetCoverPhoto();
  const reorder = useReorderBusinessPhotos();
  const photos = [...(b.photos ?? [])].sort(
    (a, z) => a.sortOrder - z.sortOrder,
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  const onUpload = async (e: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (!files.length) return;
    try {
      await upload.mutateAsync({ id: b.id, files });
      toast.success(`Uploaded ${files.length} photo(s)`);
    } catch (err: any) {
      toast.error(err?.message ?? "Upload failed");
    }
    if (inputRef.current) inputRef.current.value = "";
  };

  const onDelete = async (photoId: string) => {
    try {
      await del.mutateAsync({ id: b.id, photoId });
      toast.success("Photo removed");
    } catch (err: any) {
      toast.error(err?.message ?? "Delete failed");
    }
  };

  const onSetCover = async (photoId: string) => {
    try {
      await cover.mutateAsync({ id: b.id, photoId });
      toast.success("Thumbnail updated");
    } catch (err: any) {
      toast.error(err?.message ?? "Update failed");
    }
  };

  const onDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = photos.findIndex((p) => p.id === active.id);
    const newIdx = photos.findIndex((p) => p.id === over.id);
    if (oldIdx === -1 || newIdx === -1) return;
    const reordered = [...photos];
    const [moved] = reordered.splice(oldIdx, 1);
    reordered.splice(newIdx, 0, moved);
    try {
      await reorder.mutateAsync({
        id: b.id,
        order: reordered.map((p) => p.id),
      });
    } catch (err: any) {
      toast.error(err?.message ?? "Reorder failed");
    }
  };

  return (
    <section className="bg-card rounded-card border border-border p-5 space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 className="font-semibold">Photos</h3>
          <p className="text-xs text-muted-foreground">
            First = thumbnail · positions 2-3 = detail · rest = gallery. Drag to
            reorder.
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => inputRef.current?.click()}
          disabled={upload.isPending}
          className="shrink-0"
        >
          <Upload className="w-4 h-4 mr-1.5" />
          {upload.isPending ? "Uploading…" : "Upload"}
        </Button>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple
          hidden
          onChange={onUpload}
        />
      </div>

      {photos.length === 0 ? (
        <p className="text-sm text-muted-foreground">No photos yet.</p>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={onDragEnd}
        >
          <SortableContext
            items={photos.map((p) => p.id)}
            strategy={rectSortingStrategy}
          >
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {photos.map((p, i) => (
                <PhotoTile
                  key={p.id}
                  photo={p}
                  index={i}
                  onDelete={() => onDelete(p.id)}
                  onSetCover={() => onSetCover(p.id)}
                  delPending={del.isPending}
                  coverPending={cover.isPending}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
    </section>
  );
};
